import { Link, NavLink } from 'react-router-dom';

import { Detail, Field, Notice, Tag } from '../../../common/components/detail/detail';
import { Gate } from '../../../global/contexts/auth/auth';
import PlansOverview from '../../plans/layouts/overview';
import { useProject } from '../contexts/projects';

import '../../../common/components/detail/styles/detail.css';
import '../styles/workspace.css';

// The project as a place to work, not a row of a CRUD.
//
// Everything about a project is reachable from here: what is planned, how it
// was split, what is pending. Planeación and Desglose mount empty on purpose —
// they arrive with T07.04 and T07.05 — but their tabs exist now so the router
// is not rebuilt three times, and so the screen can say what is coming instead
// of pretending the project has nothing.

const TABS = [
    { key: 'resumen',    title: 'Resumen',    domain: 'projects:read' },
    { key: 'planeacion', title: 'Planeación', domain: 'plans:read' },
    { key: 'desglose',   title: 'Desglose',   domain: 'breakdown:read' },
    { key: 'tareas',     title: 'Tareas',     domain: 'tasks:read' },
    { key: 'actividad',  title: 'Actividad',  domain: 'events:read' },
];

const STATE_LABEL = { active: 'Activo', paused: 'Pausado', closed: 'Cerrado' };
const STATE_TONE  = { active: 'active', paused: 'warn', closed: 'neutral' };

export default function ProjectWorkspace({ projectId, tab = 'resumen' }) {
    const { data, isLoading, error } = useProject(projectId);

    if (isLoading) return <p className="client-form__loading">Cargando proyecto...</p>;

    if (error) {
        return (
            <p className="client-form__failure">
                {error.status === 404
                    ? 'Este proyecto no está disponible: no existe, o queda fuera de tu alcance.'
                    : error.message || 'No se pudo cargar el proyecto'}
            </p>
        );
    }

    // The detail answers the whole project in one response — client,
    // repositories, tiers, teams and directives. Read defensively: the contract
    // documents the request bodies and not the response shapes, so a block that
    // is not there reads as absent instead of throwing.
    const project = data?.project ?? data;
    const repositories = data?.repositories ?? project?.repositories ?? [];
    const tiers = data?.tiers ?? project?.tiers ?? [];
    const client = data?.client ?? project?.client ?? null;

    return (
        <section className="workspace">
            <header className="detail-head">
                <div className="detail-head__text">
                    <h2 className="detail-head__title">{project.name}</h2>
                    <div className="detail-head__tags">
                        <Tag>Proyecto #{project.id}</Tag>
                        {project.project_state && (
                            <Tag tone={STATE_TONE[project.project_state] ?? 'neutral'}>
                                {STATE_LABEL[project.project_state] ?? project.project_state}
                            </Tag>
                        )}
                        {client?.name && <Tag>{client.name}</Tag>}
                    </div>
                </div>

                <div className="detail-head__actions">
                    <Link to="/admin/projects" className="detail-head__action detail-head__action--quiet">
                        Volver
                    </Link>
                    {/* Configuration is a secondary action and not a tab: it is
                        where somebody goes once, not where the work happens. */}
                    <Gate domain="projects:write">
                        <Link
                            to={`/admin/projects/${project.id}/edit`}
                            className="detail-head__action detail-head__action--quiet"
                        >
                            Configuración
                        </Link>
                    </Gate>
                </div>
            </header>

            {/* A tab nobody can open is not drawn. The permission is the one the
                backend enforces on the routes that tab reads. */}
            <nav className="workspace__tabs" aria-label="Secciones del proyecto">
                {TABS.map(item => (
                    <Gate key={item.key} domain={item.domain}>
                        <NavLink
                            end={item.key === 'resumen'}
                            to={item.key === 'resumen'
                                ? `/admin/projects/${project.id}`
                                : `/admin/projects/${project.id}/${item.key}`}
                            className={({ isActive }) => (
                                `workspace__tab ${isActive ? 'workspace__tab--active' : ''}`
                            )}
                        >
                            {item.title}
                        </NavLink>
                    </Gate>
                ))}
            </nav>

            <div className="workspace__panel">
                {tab === 'resumen' && (
                    <Summary project={project} repositories={repositories} tiers={tiers} client={client} />
                )}
                {tab === 'planeacion' && <PlansOverview projectId={project.id} />}
                {tab === 'desglose' && <BreakdownTab tiers={tiers} />}
                {tab === 'tareas' && <TasksTab projectId={project.id} />}
                {tab === 'actividad' && <ActivityTab projectId={project.id} />}
            </div>
        </section>
    );
}

function Summary({ project, repositories, tiers, client }) {
    return (
        <>
            <Detail>
                <Field label="Cliente" value={client?.name} />
                <Field label="Slug" value={project.slug} mono />
                <Field label="Descripción" value={project.description} empty="Sin descripción" wide />
                <Field
                    label="Repositorios"
                    value={repositories.length
                        ? repositories.map(r => r.name || `#${r.id}`).join(' · ')
                        : null}
                    empty="Ninguno cargado"
                    wide
                />
            </Detail>

            {/* ACCESSES WITHOUT NUMBERS, ON PURPOSE.
                There is no endpoint that counts versions pending review, tiers
                waiting to be agreed or proposals waiting for authorization.
                Counting the first page would produce a figure that is wrong the
                day there are more than a hundred rows — and wrong quietly. An
                access that says what it leads to works just as well and does
                not lie. The figures arrive when the backend does. */}
            <section className="workspace__pending">
                <h3 className="workspace__pending-title">Qué está esperando a alguien</h3>
                <p className="workspace__pending-note">
                    Sin cifras: el backend todavía no publica contadores por proyecto, y contar
                    una página daría un número equivocado en cuanto haya más de cien filas.
                </p>
                <ul className="workspace__pending-list">
                    <Gate domain="plans:read">
                        <li>
                            <Link to={`/admin/projects/${project.id}/planeacion`}>
                                Versiones de plan por revisar
                            </Link>
                        </li>
                    </Gate>
                    <Gate domain="breakdown:read">
                        <li>
                            <Link to={`/admin/projects/${project.id}/desglose`}>
                                Tiers por acordar y propuestas por autorizar
                            </Link>
                        </li>
                    </Gate>
                    <Gate domain="tasks:read">
                        <li>
                            <Link to={`/admin/reviews?project=${project.id}`}>
                                Entregas esperando revisión
                            </Link>
                        </li>
                    </Gate>
                </ul>
            </section>

            <section className="workspace__tiers">
                <h3 className="workspace__pending-title">Tiers</h3>
                {tiers.length === 0
                    ? <p className="workspace__pending-note">Este proyecto todavía no tiene tiers.</p>
                    : (
                        <ul className="workspace__tier-list">
                            {tiers.map(tier => (
                                <li key={tier.id}>
                                    <Link to={`/admin/tiers/${tier.id}`} className="workspace__tier">
                                        <span className="workspace__tier-name">{tier.name}</span>
                                        {tier.breakdown_state && (
                                            <span className="workspace__tier-meta">{tier.breakdown_state}</span>
                                        )}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
            </section>
        </>
    );
}

// The breakdown is per tier, so this tab is the way in to each one rather than
// a screen of its own: the agreement, the proposals and the authorization all
// belong to a single tier and its plan version.
function BreakdownTab({ tiers }) {
    if (!tiers.length) {
        return (
            <Notice>
                Este proyecto todavía no tiene tiers. Un tier sale de una <strong>versión
                aprobada</strong> del plan, así que el primer paso está en Planeación.
            </Notice>
        );
    }

    return (
        <section className="workspace__tiers">
            <h3 className="workspace__pending-title">Elegí el tier a desglosar</h3>
            <ul className="workspace__tier-list">
                {tiers.map(tier => (
                    <li key={tier.id}>
                        <Link to={`/admin/tiers/${tier.id}/desglose`} className="workspace__tier">
                            <span className="workspace__tier-name">{tier.name}</span>
                            {tier.breakdown_state && (
                                <span className="workspace__tier-meta">
                                    {tier.breakdown_state === 'agreed' ? 'acordado'
                                        : tier.breakdown_state === 'legacy' ? 'legado'
                                        : 'borrador'}
                                </span>
                            )}
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function TasksTab({ projectId }) {
    return (
        <Notice>
            El trabajo de este proyecto se lee en{' '}
            <Link to={`/admin/development/tareas?view=list&project=${projectId}`}>la lista de tareas</Link> o en{' '}
            <Link to={`/admin/development/tareas?project=${projectId}`}>el tablero</Link>, los dos ya filtrados
            por este proyecto.
        </Notice>
    );
}

function ActivityTab({ projectId }) {
    return (
        <Notice>
            Los movimientos de este proyecto se ven en{' '}
            <Link to={`/admin/live?project=${projectId}`}>el pulso en vivo</Link>.
        </Notice>
    );
}

// Said plainly instead of drawn as an empty screen. A tab that looks broken and
// a tab that has not been built yet look identical unless one of them says so.
function ComingWith({ tarea, que }) {
    return (
        <Notice tone="accent">
            <strong>{que}</strong> todavía no está construido en el panel. Llega con la tarea
            {' '}{tarea} del tier T07. La superficie ya existe en el backend; lo que falta es esta
            pantalla — así que esto no es un error ni un proyecto vacío.
        </Notice>
    );
}
